import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import JsonHalAdapter from "traverson-hal";
import traverson from "traverson";
import get from "lodash/get";
import styled, { ThemeProvider } from "styled-components";
import { StyledEngineProvider } from "@mui/material";
import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";

import { Loading, PDFWrapper, ErrorLogger, SideEffectAutoLogout } from "commons";
import {
    isValidIncomeHistoryEvent,
    isValidIncomeHistoryInvestment
} from "commons/src/helpers/IncomeHistoryHelpers";
import { getFullClientName } from "commons/src/helpers/FormattingHelpers";
import { generateMaterialTheme } from "commons/src/helpers/MaterialThemeHelpers";
import { format, FORMAT } from "commons/src/NuDateUtils";
import { nucleus, anonymised } from "commons/src/NucleusTheme";
import { EFFECTIVE_EVENT as DEFAULT_DATE_TYPE } from "commons/src/constants/dateConstants";

import { AccountSummary } from "./AccountSummary";
import { TransactionViewerTable } from "./TransactionViewerTable";
import { CoverSheet } from "./CoverSheet";
import { fetchAccount, getMomentInIsoFormat } from "./DataFetching";
import { LOADING_STATE } from "./DataLoadingStates";
import { table2CSV } from "./Table2CSV";
import { generateFilename } from "./FilenameUtils";

export class MainFrameComponent extends Component {
    state = {
        showInvestment: {},
        startDate: this.props.dateSelection.startDate,
        endDate: this.props.dateSelection.endDate,
        results: {},
        runningCash: {},
        filterOptions: {
            "All transactions": {
                "All transactions": { selected: true, reversals: 0, transactions: 1 }
            }
        },
        filterOrdinals: [],
        searchValue: "",
        searchTotalDisplay: false,
        rawOrdinals: {},
        totalPages: this.props.isClientPortal ? 0 : LOADING_STATE.PRE,
        isCollapsed: true,
        noData: false,
        theme: this.props.theme || anonymised,
        isIncomeHistory: this.props.isIncomeHistory
    };

    componentDidMount() {
        traverson.registerMediaType(JsonHalAdapter.mediaType, JsonHalAdapter);
        this.updateIframe();

        const { accountId, isPrintMode } = this.props;
        const { startDate, endDate } = this.getDateRange();

        if (!isPrintMode) {
            this.fetchAccount(accountId, startDate, endDate, DEFAULT_DATE_TYPE);
        }
    }

    componentDidUpdate(prevProps) {
        this.updateIframe();

        const { accountId, dateSelection, isPrintMode, printingState } = this.props;

        if (prevProps.accountId !== accountId || prevProps.dateSelection.id !== dateSelection.id) {
            this.fetchAccount(
                accountId,
                dateSelection.startDate,
                dateSelection.endDate,
                DEFAULT_DATE_TYPE
            );

            this.setState({
                startDate: dateSelection.startDate,
                endDate: dateSelection.endDate
            });
        }

        if (isPrintMode && prevProps.printingState !== printingState) {
            const { startDate, endDate } = this.getDateRange();
            this.setState({ ...printingState, startDate, endDate });
        }
    }

    getDateRange = () => {
        const { isPrintMode, printingState, dateSelection } = this.props;
        return isPrintMode && printingState
            ? {
                  startDate: moment.utc(printingState.startDate),
                  endDate: moment.utc(printingState.endDate)
              }
            : {
                  startDate: dateSelection.startDate,
                  endDate: dateSelection.endDate
              };
    };

    onPrintCSV = () => table2CSV(document.querySelector("#table"), this.state);

    updateIframe = () => this.postMessage(parent);

    postMessage(_parent) {
        const scrollPadding = 40;
        const root = document.getElementById("root");
        const scrollHeight = (root ? root.scrollHeight : 0) + scrollPadding;
        _parent.postMessage(scrollHeight, "*");
    }

    fetchAccount = (accountId, startDate, endDate, dateType = DEFAULT_DATE_TYPE) => {
        if (accountId !== "default") {
            this.setState({
                totalPages: LOADING_STATE.PRE,
                isCollapsed: true,
                noData: false,
                showInvestment: {}
            });
            this.props.clearError();
            fetchAccount(
                accountId,
                startDate,
                endDate,
                dateType,
                this.state.isIncomeHistory ? this.buildFiltersForIncomeHistory : this.buildFilters,
                this.accountUpdate,
                this.cashBalanceUpdate,
                this.clientUpdate,
                this.transactionsUpdate,
                this.adviserUpdate,
                this.themeUpdate
            );
        }
    };

    accountUpdate = accountResp => this.setState({ details: accountResp });

    cashBalanceUpdate = cashResp => this.setState({ runningCash: cashResp });

    transactionsUpdate = (transactions, total) => {
        if (transactions !== null) {
            this.setState({ results: transactions, totalPages: total });
        } else {
            this.setState({ totalPages: total });
        }
    };

    clientUpdate = client => this.setState({ client: client });

    adviserUpdate = adviser => this.setState({ adviser: adviser });

    themeUpdate = theme => {
        const newTheme = {
            ...nucleus,
            primary: theme.highlightColour || nucleus.primary,
            logo: theme.logo || nucleus.logo
        };
        this.setState({ theme: newTheme });
    };

    buildFilterOrdinals = rawOrdinals =>
        Object.keys(rawOrdinals)
            .sort((a, b) => rawOrdinals[a].ordinal - rawOrdinals[b].ordinal)
            .map(high => {
                const lows = Object.keys(rawOrdinals[high].lows).sort(
                    (a, b) => rawOrdinals[high].lows[a] - rawOrdinals[high].lows[b]
                );
                if (!this.state.isIncomeHistory) {
                    lows.push("Select all");
                }
                return { high, lows };
            });

    buildFiltersForIncomeHistory = results => {
        const filterOptions = {
            ...this.state.filterOptions,
            "All transactions": {
                ...this.state.filterOptions["All transactions"]
            }
        };
        const rawOrdinals = { ...this.state.rawOrdinals };

        if (results._embedded !== undefined) {
            results._embedded.transactionEventResourceList
                .filter(trEvent => isValidIncomeHistoryEvent(trEvent))
                .reduce((a, b) => [...a, ...b.accountTransactions], [])
                .reduce((a, b) => [...a, ...b.investmentTransactions], [])
                .filter(invTrx => invTrx.itrnMappings.TRX_HIGH_LEVEL !== undefined)
                .forEach(invTrx => {
                    const highLevel = invTrx.itrnMappings.TRX_HIGH_LEVEL;
                    const lowLevel = invTrx.itrnMappings.TRX_LOW_LEVEL;
                    const description = invTrx.itrnTtypDescription;

                    if (
                        isValidIncomeHistoryInvestment(invTrx) &&
                        highLevel === "Withdrawals" &&
                        lowLevel !== "Actual payment details"
                    ) {
                        if (filterOptions[highLevel] === undefined) {
                            filterOptions[highLevel] = {};
                            rawOrdinals[highLevel] = {
                                ordinal: parseInt(invTrx.itrnMappings.TRX_HIGH_LEVEL_ORDINAL),
                                lows: {}
                            };
                        }
                        if (filterOptions[highLevel][description] === undefined) {
                            filterOptions[highLevel][description] = {
                                selected: highLevel !== "N/A",
                                transactions: 1
                            };
                            rawOrdinals[highLevel].lows[description] = parseInt(
                                invTrx.itrnMappings.TRX_LOW_LEVEL_ORDINAL
                            );
                        } else {
                            filterOptions[highLevel][description].transactions += 1;
                        }
                    }
                });
        } else {
            this.setState({ noData: true });
        }

        for (const high in filterOptions) {
            if (
                filterOptions[high] &&
                high.length > 0 &&
                filterOptions[high]["Select all"] === undefined &&
                high !== "N/A"
            ) {
                filterOptions[high]["Select all"] = {
                    selected: true,
                    transactions: 1
                };
            }
        }
        this.setState({
            filterOptions,
            filterOrdinals: this.buildFilterOrdinals(rawOrdinals),
            rawOrdinals
        });
    };

    buildFilters = results => {
        const filterOptions = this.state.filterOptions;
        const rawOrdinals = this.state.rawOrdinals;

        if (results._embedded !== undefined) {
            results._embedded.transactionEventResourceList
                .reduce((a, b) => [...a, ...b.accountTransactions], [])
                .reduce((a, b) => [...a, ...b.investmentTransactions], [])
                .filter(invTrx => invTrx.itrnMappings.TRX_HIGH_LEVEL !== undefined)
                .forEach(invTrx => {
                    const highLevel = invTrx.itrnMappings.TRX_HIGH_LEVEL;
                    const lowLevel = invTrx.itrnMappings.TRX_LOW_LEVEL;
                    const reversalIncrement = invTrx.reversal ? 1 : 0;

                    if (filterOptions[highLevel] === undefined) {
                        filterOptions[highLevel] = {};
                        rawOrdinals[highLevel] = {
                            ordinal: parseInt(invTrx.itrnMappings.TRX_HIGH_LEVEL_ORDINAL),
                            lows: {}
                        };
                    }
                    if (filterOptions[highLevel][lowLevel] === undefined) {
                        filterOptions[highLevel][lowLevel] = {
                            selected: highLevel !== "N/A",
                            reversals: reversalIncrement,
                            transactions: 1
                        };
                        rawOrdinals[highLevel].lows[lowLevel] = parseInt(
                            invTrx.itrnMappings.TRX_LOW_LEVEL_ORDINAL
                        );
                    } else {
                        filterOptions[highLevel][lowLevel].reversals += reversalIncrement;
                        filterOptions[highLevel][lowLevel].transactions += 1;
                    }
                });
        } else {
            this.setState({ noData: true });
        }

        for (const high in filterOptions) {
            if (
                filterOptions[high] &&
                high.length > 0 &&
                filterOptions[high]["Select all"] === undefined &&
                high !== "N/A"
            ) {
                filterOptions[high]["Select all"] = {
                    selected: true,
                    reversals: 0,
                    transactions: 1
                };
            }
        }
        this.setState({
            filterOptions,
            filterOrdinals: this.buildFilterOrdinals(rawOrdinals),
            rawOrdinals
        });
    };

    onDateRangeApplied = (event, dateRangePicker) => {
        this.clearResultsState();
        this.setState({ startDate: dateRangePicker.startDate, endDate: dateRangePicker.endDate });
        this.fetchAccount(
            this.props.accountId,
            dateRangePicker.startDate,
            dateRangePicker.endDate,
            dateRangePicker.dateType
        );
    };

    filterSelect = mappings => this.setState({ filterOptions: mappings });

    handleSearchInput = (value, displayTotals) =>
        this.setState({ searchValue: value, searchTotalDisplay: displayTotals });

    clearResultsState = () =>
        this.setState({
            results: {},
            filterOptions: {
                "All transactions": {
                    "All transactions": { selected: true, transactions: 1, reversals: 0 }
                }
            },
            filterOrdinals: [],
            rawOrdinals: {}
        });

    hideShowTran = (id, val) => {
        const invests = { ...this.state.showInvestment };
        invests[id] = val;
        this.setState({ showInvestment: invests, isCollapsed: !val });
    };

    toggleInvestments = () => {
        const invests = { ...this.state.showInvestment };
        get(this, "state.results._embedded.transactionEventResourceList", []).forEach(
            item => (invests[item.trevId] = this.state.isCollapsed)
        );
        this.setState({ showInvestment: invests, isCollapsed: !this.state.isCollapsed });
    };

    getClientName = () => {
        const { client } = this.state;

        return client ? getFullClientName(client) : undefined;
    };

    triggerDownload = () => {
        const { startDate, endDate } = this.state;

        const filename = generateFilename(
            this.getClientName(),
            get(this, "state.details.accountNumber", null),
            startDate,
            endDate,
            ".pdf"
        );

        this.props.onPrint(
            {
                ...this.state,
                startDate: getMomentInIsoFormat(startDate),
                endDate: getMomentInIsoFormat(endDate, true)
            },
            filename
        );
    };

    render() {
        const themeFetched = this.state.theme !== anonymised;
        const startDateFormatted = format(this.state.startDate, FORMAT.DASH_SEPARATED);
        const endDateFormatted = format(this.state.endDate, FORMAT.DASH_SEPARATED);

        const coverSheet = this.props.isPrintMode ? (
            <CoverSheet
                clientDetails={this.state.client}
                startDate={startDateFormatted}
                endDate={endDateFormatted}
                accountDetails={this.state.details}
                adviserDetails={this.state.adviser}
                logo={get(this, "state.theme.logo", "")}
            />
        ) : (
            ""
        );

        const accountSummary =
            this.props.isClientPortal || this.props.isPrintMode ? null : (
                <AccountSummary
                    clientDetails={this.state.client}
                    accountDetails={this.state.details}
                />
            );

        const materialTheme = generateMaterialTheme(this.state.theme);

        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={this.state.theme}>
                    <MUIThemeProvider theme={materialTheme}>
                        <LoadingContainer
                            data-testid="main-frame-container"
                            themeFetched={themeFetched}
                        >
                            <Loading displayWhen={themeFetched} size={25}>
                                <SideEffectAutoLogout enabled={!this.props.isClientPortal}>
                                    {accountSummary}
                                    {coverSheet}
                                    <TransactionViewerTable
                                        dateChanged={this.onDateRangeApplied}
                                        startDate={this.state.startDate}
                                        endDate={this.state.endDate}
                                        results={this.state.results}
                                        runningCash={this.state.runningCash}
                                        totalPages={this.state.totalPages}
                                        isCollapsed={this.state.isCollapsed}
                                        isError={this.props.isError}
                                        noData={this.state.noData}
                                        filterOptions={this.state.filterOptions}
                                        filterOrdinals={this.state.filterOrdinals}
                                        handleSearchInput={this.handleSearchInput}
                                        searchValue={this.state.searchValue}
                                        searchTotalDisplay={this.state.searchTotalDisplay}
                                        showInvestment={this.state.showInvestment}
                                        hideShowInvestments={this.hideShowTran}
                                        toggleInvestments={this.toggleInvestments}
                                        filterSelect={this.filterSelect}
                                        accountProductType={get(
                                            this,
                                            "state.details.wrapperType",
                                            null
                                        )}
                                        onPrintCSV={this.onPrintCSV}
                                        updateIframe={this.updateIframe}
                                        onPrint={this.props.onPrint}
                                        printState={this.props.printState}
                                        isIncomeHistory={this.state.isIncomeHistory}
                                        isClientPortal={this.props.isClientPortal}
                                        triggerDownload={this.triggerDownload}
                                    />
                                </SideEffectAutoLogout>
                            </Loading>
                        </LoadingContainer>
                    </MUIThemeProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }
}

const LoadingContainer = styled.div`
    width: 100%;
    clear: both;
    ${props => !props.themeFetched && "text-align: center;"}
`;

MainFrameComponent.propTypes = {
    accountId: PropTypes.string,
    isPrintMode: PropTypes.bool,
    pdfExporter: PropTypes.string,
    printHost: PropTypes.string,
    uuid: PropTypes.string,
    isClientPortal: PropTypes.bool,
    isIncomeHistory: PropTypes.bool,
    dateSelection: PropTypes.object,
    onPrint: PropTypes.func,
    printingState: PropTypes.object,
    printState: PropTypes.number,
    isError: PropTypes.bool,
    clearError: PropTypes.func,
    theme: PropTypes.object
};

MainFrameComponent.defaultProps = {
    isClientPortal: false,
    isIncomeHistory: false
};

export const MainFrame = PDFWrapper(ErrorLogger(MainFrameComponent), "transactions");
